import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { FileText, DownloadSimple, GraduationCap, ChartBar, PaintBrush, CheckCircle, Info, Lock, ClockCounterClockwise, Bell, XCircle, MagnifyingGlass, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import PdfPreviewModal from "../components/PdfPreviewModal";

const gradeBadgeColor = (g = "") => {
  if (g === "A") return "bg-emerald-100 text-emerald-800";
  if (g.startsWith("B")) return "bg-blue-100 text-blue-800";
  if (g.startsWith("C")) return "bg-amber-100 text-amber-800";
  if (g === "Pass") return "bg-slate-200 text-slate-800";
  if (g === "In Progress") return "bg-sky-100 text-sky-800";
  return "bg-red-100 text-red-800";
};

export default function TranscriptsPage() {
  const { user } = useAuth();
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");

  // Round 36 (#11) — programme-wide capstone propagation (the only supported
  // capstone entry path — per-course capstone UI was retired in Round 42).
  const [progCapOpen, setProgCapOpen] = useState(false);
  const [progCapForm, setProgCapForm] = useState({ programme_id: "", score: 0, note: "" });
  const [progCapSaving, setProgCapSaving] = useState(false);
  const [allProgrammes, setAllProgrammes] = useState([]);

  // Round 43 — transcript release workflow
  const [activeTab, setActiveTab] = useState("transcript");
  const [myRelease, setMyRelease] = useState(null);      // student's own status
  const [releaseRequests, setReleaseRequests] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestNote, setRequestNote] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // Round 57 — bulk approve for pending transcript requests + inline PDF preview.
  const [bulkSelection, setBulkSelection] = useState([]);   // request IDs
  const [preview, setPreview] = useState(null);             // { url, title, filename }

  // Round 44 — All-transcripts cohort view + search/filter/export
  const [allRows, setAllRows] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [filterQ, setFilterQ] = useState("");
  const [filterProgramme, setFilterProgramme] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterGrade, setFilterGrade] = useState("all");

  // Round 44 · v2 — searchable / programme-filtered student picker for Single Student tab
  const [singleQuery, setSingleQuery] = useState("");
  const [singleProgFilter, setSingleProgFilter] = useState("all");

  const isStaff = ["super_admin", "admin", "instructor"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";
  const canDesign = ["super_admin"].includes(user?.role);
  const userId = user?.id || user?._id;
  const [activeDesign, setActiveDesign] = useState(null);

  useEffect(() => {
    if (isStaff) {
      // Round 44 — only eligible (formally enrolled) students, not every registered user.
      api.get("/students/eligible").then(r => {
        // De-dupe on user id — the endpoint returns one row per enrolment.
        const seen = new Set();
        const studs = [];
        for (const s of (r.data || [])) {
          if (seen.has(s.id)) continue;
          seen.add(s.id);
          studs.push(s);
        }
        setStudents(studs);
        if (studs.length > 0) setSelectedStudent(studs[0].id);
        else setLoading(false);
      }).catch(() => setLoading(false));
      api.get("/designs/transcript/active").then(r => setActiveDesign(r.data)).catch(() => {});
      api.get("/programmes").then(r => setAllProgrammes(r.data || [])).catch(() => {});
    } else {
      setSelectedStudent(userId);
      // Round 43 — students get their release status so the UI can gate the transcript.
      api.get("/transcript-requests/me").then(r => setMyRelease(r.data)).catch(() => {});
    }
    // Round 43 — super_admins load pending/approved requests for the tab.
    if (user?.role === "super_admin") {
      loadReleaseRequests();
      // Deep-link support: /transcripts?tab=requests from the bell notif.
      try {
        const p = new URLSearchParams(window.location.search);
        const t = p.get("tab");
        if (t === "requests" || t === "all") setActiveTab(t);
      } catch (e) { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, userId, user?.role]);

  // Round 44 — load all-transcripts whenever the tab is opened or filters change.
  useEffect(() => {
    if (!isStaff || activeTab !== "all") return;
    setAllLoading(true);
    const params = new URLSearchParams();
    if (filterProgramme !== "all") params.append("programme_id", filterProgramme);
    if (filterStatus !== "all") params.append("status", filterStatus);
    if (filterGrade !== "all") params.append("grade", filterGrade);
    if (filterQ) params.append("q", filterQ);
    api.get(`/transcripts/all?${params.toString()}`)
      .then(r => { setAllRows(r.data || []); setAllLoading(false); })
      .catch(() => setAllLoading(false));
  }, [isStaff, activeTab, filterProgramme, filterStatus, filterGrade, filterQ]);

  const exportAllTranscriptsXlsx = async () => {
    try {
      const params = new URLSearchParams();
      if (filterProgramme !== "all") params.append("programme_id", filterProgramme);
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterGrade !== "all") params.append("grade", filterGrade);
      if (filterQ) params.append("q", filterQ);
      const res = await api.get(`/transcripts/all/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `all-transcripts-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${allRows.length} row(s) to Excel`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const loadReleaseRequests = () => {
    api.get("/transcript-requests").then(r => setReleaseRequests(r.data || [])).catch(() => {});
  };

  const [includeFree, setIncludeFree] = useState(false); // Task 7

  const loadTranscript = (sid) => {
    setLoading(true);
    api.get(`/transcripts/${sid}`, { params: { include_free: includeFree } })
      .then(r => { setTranscript(r.data); setLoading(false); })
      .catch(() => { setTranscript(null); setLoading(false); });
  };

  useEffect(() => {
    if (selectedStudent) loadTranscript(selectedStudent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent, includeFree]);


  const downloadPdf = () => {
    if (!selectedStudent) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/transcripts/${selectedStudent}/pdf`, "_blank");
  };

  /** Fetch the transcript PDF as a blob and open the shared preview modal.
   *  The same blob URL powers the modal's "Download" link so admins can
   *  save the exact PDF they see. */
  const openPreview = async (studentId, meta = {}) => {
    if (!studentId) return;
    try {
      const { data } = await api.get(`/transcripts/${studentId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setPreview({
        url,
        title: meta.title || (transcript?.student_name ? `${transcript.student_name} · Transcript` : "Transcript"),
        filename: meta.filename || `transcript-${studentId}.pdf`,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load transcript");
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  // Round 43 — student request transcript release.
  const submitRequest = async () => {
    setRequesting(true);
    try {
      const r = await api.post("/transcript-requests", { note: requestNote });
      setMyRelease(r.data);
      setRequestOpen(false);
      setRequestNote("");
      toast.success(r.data.status === "approved"
        ? "Transcript already released — refreshing…"
        : "Request submitted. You'll be notified once it's released.");
      if (r.data.status === "approved") loadTranscript(userId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    }
    setRequesting(false);
  };

  const approveRequest = async (req) => {
    try {
      await api.post(`/transcript-requests/${req.id}/approve`);
      toast.success(`Released transcript for ${req.student_name || req.student_email}`);
      loadReleaseRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };

  /** Approve every pending request currently in ``bulkSelection`` in sequence.
   *  Successes / failures are summarised in a single toast so the admin
   *  isn't flooded with notifications. */
  const bulkApprove = async () => {
    if (bulkSelection.length === 0) return;
    if (!window.confirm(`Approve and release ${bulkSelection.length} transcript request${bulkSelection.length === 1 ? "" : "s"}? This is permanent.`)) return;
    const successes = [];
    const failures = [];
    for (const id of bulkSelection) {
      const req = releaseRequests.find(r => r.id === id);
      const label = req?.student_name || req?.student_email || id;
      try {
        await api.post(`/transcript-requests/${id}/approve`);
        successes.push(label);
      } catch (e) {
        failures.push(`${label} (${e.response?.data?.detail || "failed"})`);
      }
    }
    if (successes.length > 0) toast.success(`Released ${successes.length} transcript${successes.length === 1 ? "" : "s"}.`);
    if (failures.length > 0) toast.error(`${failures.length} failed: ${failures.join("; ")}`);
    setBulkSelection([]);
    loadReleaseRequests();
  };

  const togglePendingSelected = (id) => {
    setBulkSelection(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  };
  const selectAllPending = () => {
    const pendingIds = releaseRequests.filter(r => r.status === "pending").map(r => r.id);
    setBulkSelection(pendingIds);
  };
  const clearPendingSelection = () => setBulkSelection([]);

  const submitReject = async () => {
    if (!rejectFor) return;
    try {
      await api.post(`/transcript-requests/${rejectFor.id}/reject`, { reason: rejectReason });
      toast.success("Request declined");
      setRejectFor(null);
      setRejectReason("");
      loadReleaseRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };

  const releaseBadge = (status) => {
    if (status === "approved") return <Badge className="rounded-none bg-emerald-100 text-emerald-800 text-[10px]">Released</Badge>;
    if (status === "pending") return <Badge className="rounded-none bg-amber-100 text-amber-800 text-[10px]">Pending review</Badge>;
    if (status === "rejected") return <Badge className="rounded-none bg-red-100 text-red-800 text-[10px]">Declined</Badge>;
    return <Badge className="rounded-none bg-slate-100 text-slate-700 text-[10px]">Not requested</Badge>;
  };

  // Round 36 (#11) — propagate one capstone score across every course in the programme.
  const saveProgrammeCapstone = async () => {
    if (!selectedStudent || !progCapForm.programme_id) {
      toast.error("Pick a programme first");
      return;
    }
    const score = Number(progCapForm.score);
    if (isNaN(score) || score < 0 || score > 100) {
      toast.error("Score must be between 0 and 100");
      return;
    }
    setProgCapSaving(true);
    try {
      const res = await api.put("/capstone-scores", {
        student_id: selectedStudent,
        programme_id: progCapForm.programme_id,
        score,
        note: progCapForm.note,
      });
      const n = res.data?.courses_updated ?? 0;
      toast.success(`Capstone propagated to ${n} course${n === 1 ? "" : "s"} — transcript will recalculate.`);
      setProgCapOpen(false);
      loadTranscript(selectedStudent);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setProgCapSaving(false);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="transcripts-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Academic Records</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Transcripts
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Grades are computed automatically from quizzes (35%), attendance (25%), feedback (10%) and capstone (30%).
            </p>
          </div>
          <div className="flex gap-2">
            {isStaff && (
              <Button
                onClick={() => { setProgCapForm({ programme_id: "", score: 0, note: "" }); setProgCapOpen(true); }}
                variant="outline"
                className="rounded-none gap-2 text-sm border-purple-300 text-purple-800 hover:bg-purple-50"
                data-testid="programme-capstone-btn"
              >
                <GraduationCap size={14} /> Programme Capstone
              </Button>
            )}
            {transcript && !transcript.locked && (
              <>
                <Button
                  onClick={() => openPreview(selectedStudent, { title: `${transcript.student_name || "Student"} · Transcript`, filename: `transcript-${transcript.student_name || selectedStudent}.pdf` })}
                  variant="outline"
                  className="rounded-none gap-2 text-sm border-blue-300 text-blue-800 hover:bg-blue-50"
                  data-testid="preview-transcript-btn"
                >
                  <Eye size={14} /> Preview PDF
                </Button>
                <Button onClick={downloadPdf} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2 text-sm" data-testid="download-transcript-btn">
                  <DownloadSimple size={14} /> Download PDF
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Round 43 — staff tab switcher between Transcript, All Transcripts (Round 44) and Requests */}
        {isStaff && (
          <div className="flex border-b border-slate-200 mb-6" data-testid="transcript-tabs">
            <button
              onClick={() => setActiveTab("transcript")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === "transcript" ? "border-blue-800 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid="tab-transcript"
            >
              Single Student
            </button>
            <button
              onClick={() => setActiveTab("all")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === "all" ? "border-blue-800 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid="tab-all-transcripts"
            >
              All Transcripts
            </button>
            {isSuperAdmin && (
              <button
                onClick={() => { setActiveTab("requests"); loadReleaseRequests(); }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${activeTab === "requests" ? "border-blue-800 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                data-testid="tab-requests"
              >
                <Bell size={14} /> Requests
                {releaseRequests.filter(r => r.status === "pending").length > 0 && (
                  <Badge className="rounded-none bg-red-500 text-white text-[10px]" data-testid="pending-requests-badge">
                    {releaseRequests.filter(r => r.status === "pending").length}
                  </Badge>
                )}
              </button>
            )}
          </div>
        )}

        {isStaff && (
          <Card className="rounded-none border-amber-200 bg-amber-50/50 mb-6" data-testid="active-transcript-template-card">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-amber-400 text-slate-950 w-10 h-10 flex items-center justify-center"><PaintBrush size={20} weight="bold" /></div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Active Transcript Template</p>
                  {activeDesign?.is_default ? (
                    <p className="text-sm font-semibold text-slate-900 truncate" data-testid="active-transcript-template-name">Default (built-in) — no custom design saved yet</p>
                  ) : (
                    <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2" data-testid="active-transcript-template-name">
                      <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                      {activeDesign?.name || "Active design"}
                      {activeDesign?.version && <Badge className="rounded-none text-[10px] bg-blue-100 text-blue-800">v{activeDesign.version}</Badge>}
                    </p>
                  )}
                  <p className="text-xs text-slate-600 mt-0.5">All transcript PDFs use this design automatically.</p>
                </div>
              </div>
              {canDesign && (
                <Link to="/admin/designers/transcript">
                  <Button variant="outline" className="rounded-none border-amber-400 text-amber-800 hover:bg-amber-100 gap-2 text-xs" data-testid="open-transcript-designer-btn">
                    <PaintBrush size={14} /> Edit / Apply Template
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}

        {/* Weighting card — visible to everyone */}
        {activeTab === "transcript" && (<>
        <Card className="border border-slate-200 rounded-none mb-6 bg-slate-50">
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Scoring Weights</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="border-l-2 border-blue-700 pl-3">
                <p className="text-xs text-slate-500">Assessment</p>
                <p className="font-bold text-blue-800">35%</p>
                <p className="text-[10px] text-slate-500 leading-tight">Quizzes + completion (auto)</p>
              </div>
              <div className="border-l-2 border-emerald-600 pl-3">
                <p className="text-xs text-slate-500">Attendance</p>
                <p className="font-bold text-emerald-700">25%</p>
                <p className="text-[10px] text-slate-500 leading-tight">% present at sessions (auto)</p>
              </div>
              <div className="border-l-2 border-amber-500 pl-3">
                <p className="text-xs text-slate-500">Feedback</p>
                <p className="font-bold text-amber-700">10%</p>
                <p className="text-[10px] text-slate-500 leading-tight">Course feedback filled (auto)</p>
              </div>
              <div className="border-l-2 border-purple-600 pl-3">
                <p className="text-xs text-slate-500">Capstone</p>
                <p className="font-bold text-purple-700">30%</p>
                <p className="text-[10px] text-slate-500 leading-tight">Shadow + project + presentation {isStaff && <span className="text-purple-700 font-semibold">(staff entry)</span>}</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              <strong>Grade bands:</strong> 91+ = <span className="text-emerald-700 font-semibold">A</span> · 81–90 = <span className="text-blue-700 font-semibold">B Upper Merit</span> · 71–80 = <span className="text-amber-700 font-semibold">C Lower Merit</span> · 61–70 = <span className="text-slate-700 font-semibold">Pass</span> · &lt;61 = <span className="text-red-700 font-semibold">D</span>
            </p>
          </CardContent>
        </Card>

        {isStaff && students.length > 0 && (() => {
          // Round 44 · v2 — filter the eligible list by programme + free-text search.
          const q = singleQuery.trim().toLowerCase();
          const filtered = students.filter(s => {
            if (singleProgFilter !== "all" && s.programme_id !== singleProgFilter) return false;
            if (!q) return true;
            return (s.name || "").toLowerCase().includes(q)
              || (s.email || "").toLowerCase().includes(q)
              || (s.admission_number || "").toLowerCase().includes(q)
              || (s.id || "").toLowerCase().includes(q);
          });
          const selectedName = students.find(s => s.id === selectedStudent);
          return (
            <div className="mb-6 border border-slate-200 p-4 bg-white shadow-sm max-w-3xl" data-testid="single-student-picker">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Select Student <span className="text-[10px] text-slate-400 normal-case tracking-normal">(payment-confirmed enrolments only)</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                <div className="relative sm:col-span-2">
                  <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={singleQuery}
                    onChange={e => setSingleQuery(e.target.value)}
                    placeholder="Search by name · email · admission #"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-800"
                    data-testid="single-student-search"
                  />
                </div>
                <Select value={singleProgFilter} onValueChange={setSingleProgFilter}>
                  <SelectTrigger className="rounded-none text-sm" data-testid="single-student-prog-filter">
                    <SelectValue placeholder="All programmes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All programmes</SelectItem>
                    {allProgrammes.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.title}` : p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[10px] text-slate-500 mb-2">{filtered.length} of {students.length} match{filtered.length === 1 ? "es" : ""} your filters</p>
              <div className="max-h-64 overflow-y-auto border border-slate-100" data-testid="single-student-list">
                {filtered.length === 0 ? (
                  <p className="text-xs text-slate-400 p-4 text-center">No student matches. Try a different search or clear the programme filter.</p>
                ) : filtered.slice(0, 200).map((s, i) => {
                  const active = s.id === selectedStudent;
                  return (
                    <button
                      key={`${s.id}-${s.programme_id || i}`}
                      onClick={() => setSelectedStudent(s.id)}
                      className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 text-sm ${active ? "bg-blue-50 text-blue-900" : "hover:bg-slate-50 text-slate-800"}`}
                      data-testid={`single-student-option-${i}`}
                    >
                      <div className="flex justify-between items-baseline gap-3">
                        <span className="font-medium truncate">{s.name || s.email}</span>
                        {s.admission_number && <span className="text-[10px] font-mono text-emerald-800 whitespace-nowrap">{s.admission_number}</span>}
                      </div>
                      <div className="flex justify-between items-baseline gap-3 text-[10px] text-slate-500">
                        <span className="truncate">{s.email}</span>
                        {s.programme_code && <span className="whitespace-nowrap">{s.programme_code}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedStudent && selectedName && (
                <p className="text-[11px] text-slate-500 mt-2">
                  Viewing transcript for <b className="text-slate-900">{selectedName.name || selectedName.email}</b>
                  {selectedName.admission_number ? ` · ${selectedName.admission_number}` : ""}
                </p>
              )}
            </div>
          );
        })()}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-100 animate-pulse" />)}
          </div>
        ) : (!isStaff && transcript && transcript.locked) ? (
          /* Round 43 — Student locked view: header only + request card */
          <>
            <Card className="border border-slate-200 rounded-none shadow-sm mb-6" data-testid="student-transcript-header">
              <CardContent className="p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {transcript.student_name}
                    </h2>
                    <p className="text-sm text-slate-500">{transcript.student_email}</p>
                    {transcript.admission_number && (
                      <p className="text-xs text-emerald-800 mt-1 font-mono">
                        Admission No: <b>{transcript.admission_number}</b>
                      </p>
                    )}
                  </div>
                  <div>{releaseBadge(myRelease?.status || transcript.release_status || "none")}</div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-amber-300 bg-amber-50/40 rounded-none" data-testid="request-transcript-card">
              <CardContent className="p-8 text-center">
                <Lock size={44} weight="fill" className="text-amber-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Your official transcript is available on request
                </h3>
                {(!myRelease || myRelease.status === "none") && (
                  <>
                    <p className="text-sm text-slate-600 max-w-md mx-auto mb-5">
                      Detailed scores, GPA and PDF download unlock once a super administrator releases your transcript.
                    </p>
                    <Button
                      onClick={() => { setRequestNote(""); setRequestOpen(true); }}
                      className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2"
                      data-testid="request-transcript-btn"
                    >
                      <FileText size={14} /> Request Transcript
                    </Button>
                  </>
                )}
                {myRelease?.status === "pending" && (
                  <>
                    <p className="text-sm text-slate-600 max-w-md mx-auto mb-3">
                      Your request is with the admin. You&apos;ll get a notification the moment it&apos;s released.
                    </p>
                    <p className="text-xs text-slate-500">
                      Requested on <b>{myRelease.requested_at ? new Date(myRelease.requested_at).toLocaleString() : "—"}</b>
                    </p>
                  </>
                )}
                {myRelease?.status === "rejected" && (
                  <>
                    <p className="text-sm text-slate-600 max-w-md mx-auto mb-2">
                      Your last request was declined{myRelease.reject_reason ? `: "${myRelease.reject_reason}"` : "."}
                    </p>
                    <Button
                      onClick={() => { setRequestNote(""); setRequestOpen(true); }}
                      className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2 mt-3"
                      data-testid="request-transcript-again-btn"
                    >
                      <FileText size={14} /> Submit a new request
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        ) : !transcript || transcript.records.length === 0 ? (
          <div className="text-center py-16">
            <FileText size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No academic records found yet. Records appear once a student starts a course or has manual capstone entries.</p>
            {isStaff && transcript && students.length > 0 && (
              <Button
                onClick={() => { setProgCapForm({ programme_id: "", score: 0, note: "" }); setProgCapOpen(true); }}
                className="bg-purple-700 text-white hover:bg-purple-800 rounded-none mt-4 gap-2 text-sm"
                data-testid="add-capstone-empty-btn"
              >
                <GraduationCap size={14} /> Enter Programme Capstone
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Header card */}
            <Card className="border border-slate-200 rounded-none shadow-sm mb-6">
              <CardContent className="p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {transcript.student_name}
                    </h2>
                    <p className="text-sm text-slate-500">{transcript.student_email}</p>
                    {transcript.admission_number && (
                      <p className="text-xs text-emerald-800 mt-1 font-mono" data-testid="transcript-admission-number">
                        Admission No: <b>{transcript.admission_number}</b>
                      </p>
                    )}
                    <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer mt-2">
                      <input type="checkbox" checked={includeFree} onChange={e => setIncludeFree(e.target.checked)} data-testid="transcript-include-free" />
                      Include free courses (paid programme by default)
                    </label>
                  </div>
                  <div className="flex gap-5">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-800">{transcript.overall_average}</p>
                      <p
                        className="text-xs text-slate-500 uppercase tracking-wider inline-flex items-center gap-1 cursor-help"
                        title="Weighted composite: Assessment 35% · Attendance 25% · Feedback 10% · Capstone 30%"
                        data-testid="overall-score-tooltip"
                      >
                        Overall
                        <Info size={11} className="text-slate-400" />
                      </p>
                    </div>
                    <div className="text-center">
                      <Badge className={`rounded-none text-base px-3 py-1 ${gradeBadgeColor(transcript.overall_grade)}`}>
                        {transcript.overall_grade}
                      </Badge>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">Classification</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-800">{transcript.course_count}</p>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Courses</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Records table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transcript-table">
                <thead>
                  <tr className="bg-slate-950 text-white">
                    <th className="text-left px-4 py-3 font-medium">Course</th>
                    <th className="text-center px-3 py-3 font-medium">Assessment <span className="block text-[9px] opacity-70">(35%)</span></th>
                    <th className="text-center px-3 py-3 font-medium">Attendance <span className="block text-[9px] opacity-70">(25%)</span></th>
                    <th className="text-center px-3 py-3 font-medium">Feedback <span className="block text-[9px] opacity-70">(10%)</span></th>
                    <th className="text-center px-3 py-3 font-medium">Capstone <span className="block text-[9px] opacity-70">(30%)</span></th>
                    <th className="text-center px-3 py-3 font-medium">Total</th>
                    <th className="text-center px-3 py-3 font-medium">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {transcript.records.map((r, i) => (
                    <tr key={r.course_id} className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`} data-testid={`transcript-row-${i}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        <p>{r.course_title}</p>
                        <p className="text-[10px] text-slate-400">{r.lessons_completed}/{r.total_lessons} lessons</p>
                      </td>
                      <td className="text-center px-3 py-3">
                        <p className="text-slate-800 font-semibold">{r.assessment_weighted}</p>
                        <p className="text-[10px] text-slate-400">Quiz {r.avg_quiz_score}% · Done {r.completion_pct}%</p>
                      </td>
                      <td className="text-center px-3 py-3">
                        <p className="text-slate-800 font-semibold">{r.attendance_weighted}</p>
                        <p className="text-[10px] text-slate-400">{r.attendance_present}/{r.attendance_meetings} present</p>
                      </td>
                      <td className="text-center px-3 py-3">
                        <p className={`font-semibold ${r.feedback_filled ? "text-emerald-700" : "text-amber-700"}`}>
                          {r.feedback_weighted}
                        </p>
                        <p className="text-[10px] text-slate-400">{r.feedback_filled ? "Filled" : "Pending"}</p>
                      </td>
                      <td className="text-center px-3 py-3">
                        <p className="text-slate-800 font-semibold">{r.capstone_weighted}</p>
                        <p className="text-[10px] text-slate-400">{r.capstone_score} / 100</p>
                      </td>
                      <td className="text-center px-3 py-3 font-bold text-slate-950">{r.total}</td>
                      <td className="text-center px-3 py-3">
                        <Badge className={`rounded-none text-[10px] ${gradeBadgeColor(r.grade)}`}>{r.grade}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total summary */}
            <div className="mt-4 flex items-center justify-end gap-3 text-sm">
              <ChartBar size={16} className="text-slate-500" />
              <span className="text-slate-600">Average across {transcript.course_count} course{transcript.course_count !== 1 ? "s" : ""}:</span>
              <span className="font-bold text-slate-950">{transcript.overall_average}</span>
              <Badge className={`rounded-none ${gradeBadgeColor(transcript.overall_grade)}`}>{transcript.overall_grade}</Badge>
            </div>
          </>
        )}
        </>)}

        {/* Round 44 — All Transcripts (cohort table) */}
        {isStaff && activeTab === "all" && (
          <div data-testid="all-transcripts-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Search</Label>
                <Input
                  value={filterQ}
                  onChange={e => setFilterQ(e.target.value)}
                  placeholder="Name · email · student ID · admission #"
                  className="mt-1 rounded-none"
                  data-testid="all-transcripts-search"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Programme</Label>
                <Select value={filterProgramme} onValueChange={setFilterProgramme}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="all-transcripts-programme">
                    <SelectValue placeholder="All programmes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All programmes</SelectItem>
                    {allProgrammes.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.title}` : p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="all-transcripts-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Grade band</Label>
                <Select value={filterGrade} onValueChange={setFilterGrade}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="all-transcripts-grade">
                    <SelectValue placeholder="All grades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All grades</SelectItem>
                    <SelectItem value="A">A (91+)</SelectItem>
                    <SelectItem value="B">B Upper Merit (81–90)</SelectItem>
                    <SelectItem value="C">C Lower Merit (71–80)</SelectItem>
                    <SelectItem value="Pass">Pass (61–70)</SelectItem>
                    <SelectItem value="D">D (&lt;61)</SelectItem>
                    <SelectItem value="InProgress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-xs text-slate-500" data-testid="all-transcripts-count">
                {allLoading ? "Loading…" : `${allRows.length} student${allRows.length === 1 ? "" : "s"} match your filters`}
              </p>
              <Button
                onClick={exportAllTranscriptsXlsx}
                disabled={allLoading || allRows.length === 0}
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none gap-2 text-sm"
                data-testid="export-all-transcripts-btn"
              >
                <DownloadSimple size={14} /> Export Excel
              </Button>
            </div>
            <div className="overflow-x-auto border border-slate-200">
              <table className="w-full text-sm" data-testid="all-transcripts-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Student</th>
                    <th className="text-left px-3 py-2 font-medium">Admission No</th>
                    <th className="text-left px-3 py-2 font-medium">Programme</th>
                    <th className="text-center px-3 py-2 font-medium">Courses</th>
                    <th className="text-center px-3 py-2 font-medium">GPA</th>
                    <th className="text-center px-3 py-2 font-medium">Grade</th>
                    <th className="text-center px-3 py-2 font-medium">Overall %</th>
                    <th className="text-center px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allLoading && (
                    <tr><td colSpan={8} className="text-center py-8 text-slate-400 text-xs">Loading transcripts…</td></tr>
                  )}
                  {!allLoading && allRows.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-10 text-slate-400 text-xs" data-testid="all-transcripts-empty">
                      No eligible students match your filters. Only students with an approved application, active enrolment and completed payment appear here.
                    </td></tr>
                  )}
                  {!allLoading && allRows.map((r, i) => (
                    <tr
                      key={`${r.student_id}-${r.programme_id}`}
                      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"}`}
                      onClick={() => { setSelectedStudent(r.student_id); setActiveTab("transcript"); }}
                      data-testid={`all-transcripts-row-${i}`}
                    >
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-slate-900">{r.student_name || "—"}</p>
                        <p className="text-[10px] text-slate-500">{r.student_email}</p>
                      </td>
                      <td className="px-3 py-2.5 text-[11px] font-mono text-emerald-800">{r.admission_number || "—"}</td>
                      <td className="px-3 py-2.5">
                        <p className="text-slate-800 text-xs">{r.programme_code || ""}</p>
                        <p className="text-[10px] text-slate-500 truncate max-w-[220px]">{r.programme_title}</p>
                      </td>
                      <td className="text-center px-3 py-2.5 text-xs text-slate-700">{r.courses_done}/{r.courses_total}</td>
                      <td className="text-center px-3 py-2.5 font-semibold">{r.gpa}</td>
                      <td className="text-center px-3 py-2.5">
                        <Badge className={`rounded-none text-[10px] ${gradeBadgeColor(r.grade)}`}>{r.grade}</Badge>
                      </td>
                      <td className="text-center px-3 py-2.5 text-slate-800">{r.overall_average}</td>
                      <td className="text-center px-3 py-2.5">
                        <Badge className={`rounded-none text-[10px] ${r.status === "Completed" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                          {r.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Round 43 — Requests tab (super_admin only) */}
        {isSuperAdmin && activeTab === "requests" && (
          <div className="space-y-3" data-testid="requests-panel">
            <p className="text-sm text-slate-600 mb-4">
              Students request access to their transcript below. Approving is <strong>permanent</strong> — the student can view and download their transcript from that point onward.
            </p>
            {releaseRequests.filter(r => r.status === "pending").length > 0 && (
              <div className="flex items-center justify-between p-3 border border-blue-200 bg-blue-50" data-testid="requests-bulk-bar">
                <div className="text-xs text-blue-900">
                  <b>{bulkSelection.length}</b> of <b>{releaseRequests.filter(r => r.status === "pending").length}</b> pending selected
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllPending} className="text-xs text-blue-800 hover:underline" data-testid="requests-select-all">
                    Select all pending
                  </button>
                  <span className="text-slate-300">|</span>
                  <button type="button" onClick={clearPendingSelection} className="text-xs text-slate-600 hover:underline" data-testid="requests-clear-sel">
                    Clear
                  </button>
                  <Button
                    disabled={bulkSelection.length === 0}
                    onClick={bulkApprove}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none gap-1 text-xs h-7 disabled:opacity-40"
                    data-testid="requests-bulk-approve"
                  ><CheckCircle size={12} /> Approve {bulkSelection.length || ""} selected</Button>
                </div>
              </div>
            )}
            {releaseRequests.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-slate-200" data-testid="requests-empty">
                <ClockCounterClockwise size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No transcript requests yet.</p>
              </div>
            ) : (
              releaseRequests.map((req, i) => (
                <Card key={req.id} className={`rounded-none border ${bulkSelection.includes(req.id) ? "border-blue-500 bg-blue-50/40" : "border-slate-200"}`} data-testid={`request-row-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div className="min-w-0 flex items-start gap-3">
                        {req.status === "pending" && (
                          <input
                            type="checkbox"
                            checked={bulkSelection.includes(req.id)}
                            onChange={() => togglePendingSelected(req.id)}
                            className="w-4 h-4 mt-1"
                            data-testid={`request-checkbox-${i}`}
                            aria-label={`Select ${req.student_name || req.student_email}`}
                          />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900" data-testid={`request-name-${i}`}>
                            {req.student_name || "(no name)"}
                          </p>
                          <p className="text-xs text-slate-500">{req.student_email}</p>
                          <p className="text-[11px] text-slate-500 mt-1">
                            Requested <b>{req.requested_at ? new Date(req.requested_at).toLocaleString() : "—"}</b>
                          </p>
                          {req.note && (
                            <p className="text-xs text-slate-600 mt-1 border-l-2 border-slate-300 pl-2 whitespace-pre-wrap">
                              {req.note}
                            </p>
                          )}
                          {req.status === "rejected" && req.reject_reason && (
                            <p className="text-[11px] text-red-700 mt-1">Reason: {req.reject_reason}</p>
                          )}
                          {req.reviewed_at && (
                            <p className="text-[10px] text-slate-400 mt-1">
                              Reviewed {new Date(req.reviewed_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {releaseBadge(req.status)}
                        {req.status === "pending" && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => approveRequest(req)}
                              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none gap-1 text-xs h-8"
                              data-testid={`approve-request-${i}`}
                            >
                              <CheckCircle size={12} /> Approve
                            </Button>
                            <Button
                              onClick={() => { setRejectFor(req); setRejectReason(""); }}
                              variant="outline"
                              className="rounded-none gap-1 text-xs h-8 border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`reject-request-${i}`}
                            >
                              <XCircle size={12} /> Decline
                            </Button>
                          </div>
                        )}
                        {req.status === "approved" && (
                          <Button
                            onClick={() => openPreview(req.student_id, { title: `${req.student_name || req.student_email} · Transcript`, filename: `transcript-${req.student_name || req.student_id}.pdf` })}
                            variant="outline"
                            className="rounded-none gap-1 text-xs h-8 border-blue-300 text-blue-800 hover:bg-blue-50"
                            data-testid={`preview-request-${i}`}
                          >
                            <Eye size={12} /> Preview PDF
                          </Button>
                        )}
                        {isStaff && (
                          <Button
                            variant="outline"
                            onClick={() => { setSelectedStudent(req.student_id); setActiveTab("transcript"); }}
                            className="rounded-none text-[10px] h-7"
                            data-testid={`open-transcript-${i}`}
                          >
                            View transcript
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Programme-wide Capstone dialog (staff only) — Round 36 #11 */}
        {isStaff && (
          <Dialog open={progCapOpen} onOpenChange={setProgCapOpen}>
            <DialogContent className="rounded-none sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="programme-capstone-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Programme Capstone Score</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-slate-500 -mt-1">
                Enter the capstone score once and it will propagate to <strong>every course</strong> in the selected programme for this student. Weighted at 30%.
              </p>
              <div className="space-y-3 mt-3">
                <div>
                  <Label className="text-sm">Programme</Label>
                  <Select value={progCapForm.programme_id} onValueChange={v => setProgCapForm({ ...progCapForm, programme_id: v })}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="programme-capstone-select">
                      <SelectValue placeholder="Select programme" />
                    </SelectTrigger>
                    <SelectContent>
                      {allProgrammes.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.title}` : p.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Score (0–100)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={progCapForm.score}
                    onChange={e => setProgCapForm({ ...progCapForm, score: e.target.value })}
                    className="mt-1 rounded-none"
                    placeholder="e.g. 85"
                    data-testid="programme-capstone-score-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">Note (optional)</Label>
                  <Textarea
                    value={progCapForm.note}
                    onChange={e => setProgCapForm({ ...progCapForm, note: e.target.value })}
                    rows={2}
                    className="mt-1 rounded-none"
                    placeholder="e.g. Strong overall capstone delivery across all courses."
                    data-testid="programme-capstone-note-input"
                  />
                </div>
                <Button
                  onClick={saveProgrammeCapstone}
                  disabled={progCapSaving}
                  className="w-full bg-purple-700 text-white hover:bg-purple-800 rounded-none"
                  data-testid="save-programme-capstone-btn"
                >
                  {progCapSaving ? "Propagating…" : "Save & Propagate to all programme courses"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Round 42 · Issue 4 — Per-course capstone dialog retired.
            All capstone entries now flow through Programme Capstone above. */}

        {/* Round 43 — Student request-transcript dialog */}
        <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
          <DialogContent className="rounded-none sm:max-w-md" data-testid="request-transcript-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Request your transcript</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-500 -mt-1">
              Your request goes to the super administrator. Once released, your full transcript and PDF download unlock permanently on this page.
            </p>
            <div className="space-y-3 mt-3">
              <div>
                <Label className="text-sm">Note (optional)</Label>
                <Textarea
                  value={requestNote}
                  onChange={e => setRequestNote(e.target.value)}
                  rows={3}
                  className="mt-1 rounded-none"
                  placeholder="e.g. For a university application deadline on 30 Sept."
                  data-testid="request-note-input"
                />
              </div>
              <Button
                onClick={submitRequest}
                disabled={requesting}
                className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none"
                data-testid="submit-transcript-request-btn"
              >
                {requesting ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Round 43 — Super_admin decline dialog */}
        {isSuperAdmin && (
          <Dialog open={!!rejectFor} onOpenChange={o => { if (!o) { setRejectFor(null); setRejectReason(""); } }}>
            <DialogContent className="rounded-none sm:max-w-md" data-testid="reject-request-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Decline transcript request</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-slate-500 -mt-1">
                The student will be notified. They can submit a new request afterwards.
              </p>
              <div className="space-y-3 mt-3">
                <div>
                  <Label className="text-sm">Reason (shared with the student)</Label>
                  <Textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    rows={3}
                    className="mt-1 rounded-none"
                    placeholder="e.g. Outstanding tuition balance."
                    data-testid="reject-reason-input"
                  />
                </div>
                <Button
                  onClick={submitReject}
                  className="w-full bg-red-700 text-white hover:bg-red-800 rounded-none"
                  data-testid="confirm-reject-btn"
                >
                  Decline request
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        <PdfPreviewModal
          open={!!preview}
          url={preview?.url}
          title={preview?.title}
          filename={preview?.filename}
          onClose={closePreview}
        />
      </div>
    </Layout>
  );
}
